import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import axiosInstance from "../../services/api";
import {
    formatPercent,
    hasStripeAccount,
    isStripeRevoked,
    isStripeStandard,
    isStripeExpressPending,
    stripeAccountChip,
} from "../../helpers/functions";

import { useHistory, useLocation } from "react-router-dom";
import Modal from "../../components/generic/modal";
import { RevAlert, RevButton, RevCard, RevChip, RevDanger, RevField, RevKvRow, RevSegmented, RevSkeleton, RevSwitch } from "../../components/ds";

const SUPPORTED_CURRENCIES = ['EUR', 'USD', 'MXN'];

// Una cuenta Standard es del fotógrafo: Stripe no permite crear login links sobre ella, así
// que GET /stripe-account-url devuelve esta misma constante. Aquí es un <a> y no una llamada:
// ahorra el viaje a la API y evita que Safari/Firefox bloqueen el window.open tras el await.
const STRIPE_DASHBOARD_URL = 'https://dashboard.stripe.com/';

// Forma de la pantalla que se está pidiendo. La card de Stripe cambia con el estado de la
// cuenta del fotógrafo, que no se conoce hasta que llega el usuario, así que se dibuja en
// su estado MÍNIMO (chip + párrafo + botón de vinculación, la rama sin cuenta): cualquier
// otro estado es más alto, y un esqueleto corto hace crecer la página en vez de colapsarla.
const ESQUELETO = (t) => ({
    bloques: [
        {
            card: {
                sub: t('account.sales.sale.reality'),
                campos: [[t('account.sales.currencyHelp'), t('account.sales.countryHelp')]],
                botones: [t('generic.saveButton.text')],
            },
        },
        {
            card: {
                chip: t('account.profile.stripe.none'),
                textos: [t('account.sales.stripeLinkSection')],
                botones: [t('account.sales.stripeLink')],
            },
        },
    ],
    aside: [{
        card: {
            sub: t('account.sales.payout.intro'),
            kv: [
                t('account.sales.payout.revopic'),
                t('account.sales.payout.stripeEU'),
                t('account.sales.payout.stripeUK'),
            ],
            ayudas: [
                { texto: t('account.sales.payout.detailHelp'), divisor: true },
                t('account.sales.payout.note'),
                t('account.sales.vatNote'),
            ],
        },
    }],
    clases: { cols: 'rev-cols' },
});

const AccountPhotoSale = ({ user, loadingUser, postUser, getUser, events = [] }) => {
    const { t, i18n } = useTranslation();

    const [loadingStripeAccount, setLoadingStripeAccount] = useState(false);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [showUnlinkModal, setShowUnlinkModal] = useState(false);
    const [revokeAccess, setRevokeAccess] = useState(false);
    const [linkResult, setLinkResult] = useState(null);
    const [stripeError, setStripeError] = useState(null);
    const [pending, setPending] = useState(null);
    const [saved, setSaved] = useState(false);
    const history = useHistory();
    const location = useLocation();

    const { register, watch, setValue, handleSubmit, reset, formState: { isDirty } } = useForm({
        defaultValues: {
            "id": user?.id,
            "name": user?.name,
            "email": user?.email,
            "receiveNews": user?.receiveNews,
            "lang": user?.lang,
            "currency": user?.currency,
            "country": user?.country,
            "role": "activeUser"
        }
    });

    // Actualizar el formulario cuando cambie el usuario. No se resetea sobre lo tecleado
    // salvo que haya un guardado en vuelo (si no, el LOADING_USER del propio guardado
    // pisaría los cambios del usuario).
    useEffect(() => {
        if (!user?.id) return;
        if (isDirty && !pending) return;
        reset({
            "id": user.id,
            "name": user.name,
            "email": user.email,
            "receiveNews": user.receiveNews,
            "lang": user.lang,
            "currency": user.currency,
            "country": user.country,
            "role": "activeUser"
        });
        // eslint-disable-next-line
    }, [user]);

    // El `user` de redux tiene que haber sido REEMPLAZADO desde el envío: comparar solo
    // por valor confirmaría un guardado fallido cuando lo enviado ya coincidía.
    useEffect(() => {
        if (!pending || !user) return;
        if (user === pending.user) return;
        const sent = pending.data;
        const matches = user.currency === sent.currency && user.country === sent.country;
        if (!matches) return;
        setSaved(true);
        getUser();
        setPending(null);
        // eslint-disable-next-line
    }, [user, pending]);

    useEffect(() => {
        if (!saved) return;
        let alive = true;
        const timer = setTimeout(() => {
            if (alive) setSaved(false);
        }, 4000);
        return () => {
            alive = false;
            clearTimeout(timer);
        };
    }, [saved]);

    // Retorno del callback de OAuth: /account/photo-sale/stripe-link-{success|fail|cancel}
    useEffect(() => {
        if (location.pathname === '/account/photo-sale/stripe-link-success') {
            setStripeError(null);
            setLinkResult({ kind: 'success' });
            getUser();
        } else if (location.pathname === '/account/photo-sale/stripe-link-fail') {
            setStripeError(null);
            setLinkResult({ kind: 'fail', reason: new URLSearchParams(location.search).get('reason') });
        } else if (location.pathname === '/account/photo-sale/stripe-link-cancel') {
            setLinkResult(null);
            history.replace('/account/photo-sale/home');
        }
        // eslint-disable-next-line
    }, [location.pathname, location.search]);

    const onSubmit = (data) => {
        setPending({ data, user });
        postUser(data).then((ok) => {
            if (!ok) setPending(null);
        });
    };

    // Solo para la cuenta Express (login link real, con el fallback de onboarding del backend):
    // la Standard abre el dashboard con un enlace directo.
    const goToStripeAccount = async (e) => {
        e.preventDefault();
        setLoadingStripeAccount(true);
        try {
            var res = await axiosInstance.get("/stripe-account-url");
            if (res.data?.url?.includes("https://")) window.open(res.data.url, '_blank');
            else setStripeError(t('account.sales.stripeAccessError'));
        } catch (error) {
            setStripeError(t('account.sales.stripeAccessError'));
        } finally {
            setLoadingStripeAccount(false);
        }
    }

    // La cuenta anterior solo existe si el backend manda `previousUrl`: el campo
    // desaparece del JSON cuando es null.
    const goToPreviousStripeAccount = async (e) => {
        e.preventDefault();
        setLoadingStripeAccount(true);
        try {
            const res = await axiosInstance.get("/stripe-account-url");
            if (res.data?.previousUrl?.includes("https://")) window.open(res.data.previousUrl, '_blank');
            else setStripeError(t('account.sales.stripePreviousUnavailable'));
        } catch (error) {
            setStripeError(t('account.sales.stripePreviousUnavailable'));
        } finally {
            setLoadingStripeAccount(false);
        }
    }

    const handleStartConnect = async () => {
        setShowLinkModal(false);
        setStripeError(null);
        setLoadingStripeAccount(true);
        try {
            const res = await axiosInstance.post('/start-stripe-connect');
            if (res.data?.url?.startsWith('https://')) {
                window.location.assign(res.data.url);
                return;
            }
            setStripeError(t('account.sales.stripeLinkError'));
        } catch (error) {
            setStripeError(error.response?.status === 409
                ? t('account.sales.stripeAlreadyLinked')
                : t('account.sales.stripeLinkError'));
        } finally {
            setLoadingStripeAccount(false);
        }
    };

    // El 200 con `alreadyUnlinked` es idempotente: se trata como éxito silencioso.
    const handleUnlink = async () => {
        setStripeError(null);
        setLoadingStripeAccount(true);
        try {
            await axiosInstance.delete('/unlink-stripe-account', { data: { revoke: revokeAccess } });
            setShowUnlinkModal(false);
            setRevokeAccess(false);
            getUser();
        } catch (error) {
            const code = error.response?.data?.code;
            setShowUnlinkModal(false);
            if (code === 'express_account') setStripeError(t('account.sales.stripeUnlinkExpressError'));
            else if (code === 'stripe_deauthorize_failed') setStripeError(t('account.sales.stripeUnlinkRevokeError'));
            else setStripeError(t('account.sales.stripeUnlinkError'));
        } finally {
            setLoadingStripeAccount(false);
        }
    };

    const openLinkModal = () => {
        setStripeError(null);
        setShowLinkModal(true);
    };

    const closeLinkResult = () => {
        setLinkResult(null);
        history.push('/account/photo-sale/home');
    };

    const linkFailMessage = (reason) => {
        if (reason === 'in_use') return t('account.sales.stripeLinkFailInUse');
        if (reason === 'country') return t('account.sales.stripeLinkFailCountry');
        return t('account.sales.stripeErrorTitle');
    };

    // Función para verificar si el usuario tiene configurados moneda y país
    const isStripeConfigReady = () => {
        return user?.currency?.length > 0 && user?.country?.length > 0;
    };

    if (loadingUser && !user?.id) return (
        <>
            <p className="rev-help">{t('account.sales.intro')}</p>
            <RevSkeleton.Page forma="form" label={t('event.photos.loading')} {...ESQUELETO(t)} />
        </>
    );

    const sinCuentaStripe = !hasStripeAccount(user);
    const cuentaRevocada = isStripeRevoked(user);
    const cuentaStandard = isStripeStandard(user);
    const cuentaExpress = isStripeExpressPending(user);

    // Los candados miran la cuenta PROPIA (Standard), no el mero `stripeAccountId`: la Express
    // está de salida y hay fotógrafos Express sin `country` guardado. Bloquear por id les dejaba
    // en punto muerto — sin país no se habilita «Vincular» (isStripeConfigReady) y con la Express
    // el campo País estaba deshabilitado, así que no había forma de migrar desde esta pantalla.
    const currencyLocked = Boolean(user?.hasEventsWithPricing) || cuentaStandard;
    const countryLocked = cuentaStandard;

    // La card la guía UNA sola acción, la que lleva a la cuenta propia: vincularla (o abrirla,
    // si ya es Standard). Todo lo de la cuenta que abrió Revopic baja al panel de la antigua.
    const accionPrincipal = cuentaStandard
        ? <RevButton
            as="a"
            variant="primary"
            href={STRIPE_DASHBOARD_URL}
            target="_blank"
            rel="noopener noreferrer">
            {t('account.sales.stripeAccess')}
        </RevButton>
        : <RevButton
            variant="primary"
            disabled={loadingStripeAccount || !isStripeConfigReady()}
            loading={loadingStripeAccount}
            onClick={openLinkModal}>
            {cuentaRevocada ? t('account.sales.stripeRelink') : t('account.sales.stripeLink')}
        </RevButton>;

    // La cuenta antigua es siempre la Express que abrió Revopic, y el fotógrafo la necesita para
    // su histórico y sus payouts: mientras no migra es la cuenta activa (login link de
    // /stripe-account-url); una vez migrado vive en `previousUrl` del mismo endpoint. Por eso
    // el panel sigue ahí tras vincular o tras una revocación: no se desvincula nunca.
    const cuentaAntigua = cuentaExpress
        ? { texto: t('account.sales.stripeOldActive'), abrir: goToStripeAccount }
        : user?.hasPreviousStripeAccount === true
            ? { texto: t('account.sales.stripeHistoryBody'), abrir: goToPreviousStripeAccount }
            : null;

    const chip = stripeAccountChip(user);

    const userCurrency = SUPPORTED_CURRENCIES.includes(user?.currency) ? user.currency : 'EUR';
    const userFormatter = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: userCurrency });
    const eurFormatter = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' });
    const stripeFeeEU = `${eurFormatter.format(0.25)} + ${formatPercent(0.015, i18n.language)}`;
    const stripeFeeUK = `${eurFormatter.format(0.25)} + ${formatPercent(0.025, i18n.language)}`;

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <p className="rev-help">{t('account.sales.intro')}</p>
            <div className="rev-cols">
                <div className="rev-cols__main">
                    <RevCard
                        title={t('account.sales.sale.title')}
                        sub={t('account.sales.sale.reality')}
                        foot={<>
                            {saved && <RevChip tone="ok">{t('account.sales.saved')}</RevChip>}
                            <div className="rev-card__foot-actions">
                                <RevButton
                                    data-testid="saveOrCreate"
                                    type="submit"
                                    variant="primary"
                                    disabled={!isDirty || loadingUser || currencyLocked}
                                    loading={loadingUser}>
                                    {t('generic.saveButton.text')}
                                </RevButton>
                            </div>
                        </>}>
                        <div className="rev-form-grid">
                            <RevField
                                label={t('account.sales.currencySection')}
                                help={currencyLocked ? t('account.sales.lockedHelp') : t('account.sales.currencyHelp')}>
                                <input type="hidden" data-testid="currency" {...register('currency')} />
                                <RevSegmented
                                    items={[{ value: 'EUR', label: 'EUR €' }, { value: 'USD', label: 'USD $' }, { value: 'MXN', label: 'MXN $' }]}
                                    value={watch('currency')}
                                    disabled={currencyLocked}
                                    onChange={(currency) => setValue('currency', currency, { shouldDirty: true })}
                                />
                            </RevField>
                            <RevField
                                label={t('account.sales.countrySection')}
                                help={countryLocked ? t('account.sales.lockedHelp') : t('account.sales.countryHelp')}>
                                <input type="hidden" data-testid="country" {...register('country')} />
                                <RevSegmented
                                    items={[{ value: 'ES', label: t('account.sales.country.ES') }, { value: 'PT', label: t('account.sales.country.PT') }, { value: 'MX', label: t('account.sales.country.MX') }]}
                                    value={watch('country')}
                                    disabled={countryLocked}
                                    onChange={(country) => setValue('country', country, { shouldDirty: true })}
                                />
                            </RevField>
                        </div>
                    </RevCard>

                    <RevCard
                        title={t('account.sales.stripeSection')}
                        foot={<div className="rev-card__foot-actions">{accionPrincipal}</div>}>
                        <div><RevChip tone={chip.tone}>{t(chip.key)}</RevChip></div>
                        {sinCuentaStripe &&
                            <p className="rev-card__text">{t('account.sales.stripeLinkSection')}</p>}
                        {cuentaStandard &&
                            <p className="rev-card__text">{t('account.sales.stripeManage')}</p>}
                        {cuentaRevocada &&
                            <RevAlert tone="err" title={t('account.sales.stripeRevokedTitle')}>
                                <p className="rev-card__text">{t('account.sales.stripeRevokedBody')}</p>
                            </RevAlert>}
                        {!cuentaStandard && !isStripeConfigReady() &&
                            <span className="rev-help">{t('account.sales.stripeConfigRequired')}</span>}
                        {stripeError &&
                            <RevAlert tone="err">
                                <span>{stripeError}</span>
                            </RevAlert>}
                    </RevCard>

                    {cuentaStandard &&
                        <RevDanger
                            title={t('account.sales.stripeDangerTitle')}
                            text={t('account.sales.stripeUnlinkHelp')}
                            action={
                                <RevButton
                                    variant="danger"
                                    disabled={loadingStripeAccount}
                                    loading={loadingStripeAccount}
                                    onClick={() => setShowUnlinkModal(true)}>
                                    {t('account.sales.stripeUnlink')}
                                </RevButton>
                            }
                        />}

                    {/* Panel callado, el último de la columna: papel alterno y tipografía menor para que
                        la acción que guía siga siendo la de arriba. */}
                    {cuentaAntigua &&
                        <div className="rev-antigua">
                            <div className="rev-antigua__main">
                                <span className="rev-antigua__title">{t('account.sales.stripeOldTitle')}</span>
                                <span className="rev-antigua__text">{cuentaAntigua.texto}</span>
                            </div>
                            <RevButton
                                variant="subtle"
                                size="sm"
                                disabled={loadingStripeAccount}
                                loading={loadingStripeAccount}
                                onClick={cuentaAntigua.abrir}>
                                {t('account.sales.stripeOldOpen')}
                            </RevButton>
                        </div>}
                </div>
                <div className="rev-cols__aside">
                    <RevCard title={t('account.sales.payout.title')} sub={t('account.sales.payout.intro')}>
                        <RevKvRow k={t('account.sales.payout.revopic')} v={userFormatter.format(0)} num />
                        <RevKvRow k={t('account.sales.payout.stripeEU')} v={stripeFeeEU} num />
                        <RevKvRow k={t('account.sales.payout.stripeUK')} v={stripeFeeUK} num />
                        <span className="rev-help rev-help--divider">{t('account.sales.payout.detailHelp')}</span>
                        <span className="rev-help">{t('account.sales.payout.note')}</span>
                        <span className="rev-help">{t('account.sales.vatNote')}</span>
                    </RevCard>
                </div>
            </div>

            {/* Condiciones que se aceptan al arrancar el OAuth de Stripe */}
            {showLinkModal && (
                <Modal
                    title={t('account.sales.stripeLinkModalTitle')}
                    message=""
                    showCancelButton={true}
                    showConfirmButton={true}
                    cancelButtonText={t('Cancel')}
                    confirmButtonText={t('account.sales.stripeAcceptAndConnect')}
                    onCancel={() => setShowLinkModal(false)}
                    onConfirm={handleStartConnect}
                    loading={loadingStripeAccount}
                    closable={true}
                    customComponent={
                        <div>
                            <p>{t('account.sales.stripeLinkModalMsg')}</p>
                            <ul>
                                <li>
                                    <strong>{t('account.sales.stripeCommissionRevopic')}</strong> {t('account.sales.stripeCommissionRevenuePercent')}
                                </li>
                                <li>
                                    <strong>{t('account.sales.stripeCommissionStripe')}</strong>
                                    <ul>
                                        <li>{t('account.sales.stripeCommissionEU')}</li>
                                        <li>{t('account.sales.stripeCommissionUK')}</li>
                                    </ul>
                                </li>
                            </ul>
                            <p>{t('account.sales.stripeCommissionNote')}</p>
                            <p>{t('account.sales.stripeOwnAccountNotice')}</p>
                            <p>{t('account.sales.vatNote')}</p>
                            <p>
                                <a
                                    href="https://stripe.com/pricing"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('account.sales.stripeTermsLink')}
                                </a>
                            </p>
                        </div>
                    }
                />
            )}

            {/* Desvincular: la casilla decide si además se revoca el acceso en Stripe */}
            {showUnlinkModal && (
                <Modal
                    title={t('account.sales.stripeUnlinkModalTitle')}
                    message={revokeAccess ? t('account.sales.stripeUnlinkModalMsgRevoke') : t('account.sales.stripeUnlinkModalMsg')}
                    showCancelButton={true}
                    showConfirmButton={true}
                    cancelButtonText={t('Cancel')}
                    confirmButtonText={t('account.sales.stripeUnlinkConfirm')}
                    onCancel={() => { setShowUnlinkModal(false); setRevokeAccess(false); }}
                    onConfirm={handleUnlink}
                    loading={loadingStripeAccount}
                    closable={true}
                    isDanger={true}
                    customComponent={
                        <label className="rev-switch-field" htmlFor="revokeAccess">
                            <input
                                type="checkbox"
                                id="revokeAccess"
                                data-testid="revokeAccess"
                                className="rev-switch-input"
                                checked={revokeAccess}
                                onChange={(e) => setRevokeAccess(e.target.checked)} />
                            <RevSwitch on={revokeAccess} />
                            <span>{t('account.sales.stripeUnlinkRevokeLabel')}</span>
                        </label>
                    }
                />
            )}

            {/* Vuelta del callback de Stripe */}
            {linkResult && (
                <Modal
                    title={linkResult.kind === 'success'
                        ? t('account.sales.stripeSuccessTitle')
                        : t('account.sales.stripeErrorModalTitle')}
                    message={linkResult.kind === 'success'
                        ? t('account.sales.stripeSuccessMsg')
                        : linkFailMessage(linkResult.reason)}
                    showCancelButton={linkResult.kind === 'success'}
                    cancelButtonText={events?.length > 0
                        ? t('account.sales.stripeSuccessSeeEvents')
                        : t('account.sales.stripeSuccessCreateEvent')}
                    showConfirmButton={true}
                    confirmButtonText={t('account.sales.ok')}
                    onConfirm={closeLinkResult}
                    onCancel={() => history.push(events?.length > 0 ? '/events' : '/events/new')}
                    loading={false}
                    closable={false}
                    isDanger={linkResult.kind !== 'success'}
                />
            )}
        </form>
    );
};

export default AccountPhotoSale;
